import { useState, useCallback } from 'react';

interface UseManageDealDrawerFooterProps {
    totalSteps: number;
    currentStep: number;
    onSave: () => Promise<void>;
    onNext?: () => void;
    onShowDismissalAlert?: () => void;
    disabled?: boolean;
}

interface UseManageDealDrawerFooterReturn {
    isSubmitting: boolean;
    isLastStep: boolean;
    handlePrimaryClick: (e: React.MouseEvent) => Promise<void>;
    getPrimaryButtonText: (text: string) => string;
}

export const useManageDealDrawerFooter = ({
    totalSteps,
    currentStep,
    onSave,
    onNext,
    onShowDismissalAlert,
    disabled = false,
}: UseManageDealDrawerFooterProps): UseManageDealDrawerFooterReturn => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isLastStep = currentStep >= totalSteps;
    const isEnhancedMode = !!onShowDismissalAlert;

    const getPrimaryButtonText = useCallback(
        (text: string) => {
            if (isSubmitting) return 'Processing...';
            return isLastStep ? text || 'Save & Close' : 'Proceed';
        },
        [isSubmitting, isLastStep],
    );

    const handlePrimaryClick = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();
            if (isSubmitting || disabled) return;
            setIsSubmitting(true);
            if (isEnhancedMode) {
                if (isLastStep) await onSave();
                else if (onNext) onNext();
            } else await onSave();
            setIsSubmitting(false);
        },
        [isSubmitting, disabled, isLastStep, onSave, onNext, isEnhancedMode],
    );

    return {
        isSubmitting,
        isLastStep,
        handlePrimaryClick,
        getPrimaryButtonText,
    };
};
